/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/ReportsTab.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                          |
 * |---|------------------------|-------------------|------------------|---------------------------------------|
 * | 1 | 2026-06-18 (Phase 3b)  | Claude Sonnet 4.6 | 4-tab nav        | Created — full-screen run history     |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<ReportsTab />)
 *
 * EXPORTS
 * -------
 * ReportsTab — no props required (reads all runs from Dexie directly)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Shows ALL runs across ALL test cases — not scoped to a single test.
 * 2. Filters: test name (text search), status (passed/failed/healed/error/all),
 *    sorted by most recent first.
 * 3. Expanding a run row shows per-step detail (same as old ExecutionHistory RunDetail).
 * 4. useLiveQuery re-renders automatically when new runs are saved after Execution.
 *
 * =============================================================================
 */

import { useState, useEffect } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { readScreenshotAsBase64 } from "../utils/studioReportGenerator.js";
import { db, deleteRun, deleteRuns, saveRunGroup, deleteRunGroup, moveRunsToGroup } from "../db/index.js";
import type { Run, RunStep, RunStatus, RunGroup } from "../db/schema.js";
import type { IRStep } from "@mimiq/ir";
import { downloadRunReport, downloadRunReportsAsZip } from "../utils/exportUtils.js";

/** Derive a human-readable name from an IR step (used in the Reports detail table). */
function irStepName(step: IRStep): string {
  // Steps with selector: use the description (set at record time, e.g. "Login button")
  if ("selector" in step) {
    const sel = (step as { selector?: { description?: string } }).selector;
    if (sel?.description && sel.description !== "element") return sel.description;
  }
  switch (step.type) {
    case "typeText":   return step.sensitive ? `🔒 "••••••••"` : `"${step.text}"`;
    case "wait":       return `${step.durationMs}ms`;
    case "pressKey":   return step.key;
    case "launchApp":
    case "stopApp":    return step.packageName;
    case "openUrl":
    case "deepLink":   return step.url;
    case "print":      return step.message;
    case "checkpoint": return step.message;
    case "setVariable":return step.name;
    case "swipe":      return `swipe ${step.from[0]},${step.from[1]}→${step.to[0]},${step.to[1]}`;
    case "screenshot": return step.label ?? "screenshot";
    default:           return step.type;
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusIcon(s: RunStatus): string {
  if (s === "passed") return "✓";
  if (s === "passed_healed") return "⚕";
  if (s === "failed") return "✗";
  if (s === "error") return "!";
  return "?";
}

function statusLabel(s: RunStatus): string {
  if (s === "passed") return "Passed";
  if (s === "passed_healed") return "Healed";
  if (s === "failed") return "Failed";
  if (s === "error") return "Error";
  return "Unknown";
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatDuration(startedAt: number, finishedAt: number): string {
  const ms = finishedAt - startedAt;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── Root component ────────────────────────────────────────────────────────────

export function ReportsTab() {
  const runs  = useLiveQuery<Run[]>(() => db.runs.orderBy("startedAt").reverse().toArray(), []) ?? [];
  const groups = useLiveQuery<RunGroup[]>(() => db.runGroups.orderBy("name").toArray(), []) ?? [];

  // ── Filter state ─────────────────────────────────────────────────────────
  const [filterText, setFilterText]         = useState("");
  const [filterStatus, setFilterStatus]     = useState<RunStatus | "all">("all");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo]     = useState("");
  const [filterGroupId, setFilterGroupId]   = useState<string | "ungrouped" | "all">("all");
  const [filterTag, setFilterTag]           = useState("");

  // ── Sort state ───────────────────────────────────────────────────────────
  const [sortCol, setSortCol]   = useState<"date" | "name" | "status" | "steps" | "duration">("date");
  const [sortDir, setSortDir]   = useState<"asc" | "desc">("desc");

  function toggleSort(col: typeof sortCol) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  }
  function sortIcon(col: typeof sortCol) {
    if (sortCol !== col) return <span className="rep-sort-icon rep-sort-icon--off">⇅</span>;
    return <span className="rep-sort-icon">{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  // ── Selection state ──────────────────────────────────────────────────────
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [exporting, setExporting]         = useState(false);

  // ── Group management modal ───────────────────────────────────────────────
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName]     = useState("");
  const [newGroupColor, setNewGroupColor]   = useState("#6366f1");
  const [moveToGroupId, setMoveToGroupId]   = useState<string | "new" | "none">("");

  // ── Confirmation dialog ──────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string; body: string; danger?: boolean;
    onConfirm: () => void;
  } | null>(null);

  function confirm(opts: typeof confirmDialog) { setConfirmDialog(opts); }

  // ── Filter + sort ────────────────────────────────────────────────────────
  const filtered = (() => {
    let list = runs.filter((r) => {
      if (filterText    && !r.testCaseName.toLowerCase().includes(filterText.toLowerCase()) && !(r.tags ?? []).some(t => t.includes(filterText.toLowerCase()))) return false;
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterDateFrom && r.startedAt < new Date(filterDateFrom).getTime()) return false;
      if (filterDateTo   && r.startedAt > new Date(filterDateTo).getTime() + 86_400_000) return false;
      if (filterGroupId === "ungrouped" && r.groupId) return false;
      if (filterGroupId !== "all" && filterGroupId !== "ungrouped" && r.groupId !== filterGroupId) return false;
      if (filterTag && !(r.tags ?? []).includes(filterTag)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      let v = 0;
      if (sortCol === "date")     v = a.startedAt - b.startedAt;
      if (sortCol === "name")     v = a.testCaseName.localeCompare(b.testCaseName);
      if (sortCol === "status")   v = a.status.localeCompare(b.status);
      if (sortCol === "steps")    v = a.stepCount - b.stepCount;
      if (sortCol === "duration") v = (a.finishedAt - a.startedAt) - (b.finishedAt - b.startedAt);
      return sortDir === "asc" ? v : -v;
    });
    return list;
  })();

  const checkedRuns = filtered.filter(r => checkedIds.has(r.id));
  const allChecked  = filtered.length > 0 && filtered.every(r => checkedIds.has(r.id));
  const allTags     = [...new Set(runs.flatMap(r => r.tags ?? []))].sort();

  function toggleCheck(id: string) {
    setCheckedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleAll() {
    if (allChecked) setCheckedIds(prev => { const n = new Set(prev); filtered.forEach(r => n.delete(r.id)); return n; });
    else setCheckedIds(prev => new Set([...prev, ...filtered.map(r => r.id)]));
  }
  function clearSelection() { setCheckedIds(new Set()); }

  // ── Delete handlers ──────────────────────────────────────────────────────
  function handleDeleteOne(run: Run) {
    confirm({
      title: "Delete this run?",
      body: `Delete the run of "${run.testCaseName}" from ${formatDate(run.startedAt)}? This cannot be undone.`,
      onConfirm: async () => { await deleteRun(run.id); setConfirmDialog(null); if (expandedRunId === run.id) setExpandedRunId(null); },
    });
  }

  function handleDeleteSelected() {
    const n = checkedRuns.length;
    confirm({
      title: n >= 10 ? `⚠ Delete ${n} runs?` : `Delete ${n} run${n !== 1 ? "s" : ""}?`,
      body: n >= 10
        ? `Permanently delete ${n} runs? This is a large deletion and cannot be undone.`
        : `Delete these ${n} run${n !== 1 ? "s" : ""}? This cannot be undone.`,
      danger: n >= 5,
      onConfirm: async () => { await deleteRuns(checkedRuns.map(r => r.id)); clearSelection(); setConfirmDialog(null); },
    });
  }

  function handleDeleteAllFiltered() {
    const n = filtered.length;
    confirm({
      title: `⚠ Delete all ${n} filtered runs?`,
      body: `Permanently delete all ${n} runs matching the current filters? This cannot be undone.`,
      danger: true,
      onConfirm: async () => { await deleteRuns(filtered.map(r => r.id)); setConfirmDialog(null); },
    });
  }

  function handleDeleteGroup(g: RunGroup) {
    const count = runs.filter(r => r.groupId === g.id).length;
    confirm({
      title: `Delete group "${g.name}"?`,
      body: count > 0
        ? `This group contains ${count} run${count !== 1 ? "s" : ""}. Choose what happens to them:`
        : `Delete the empty group "${g.name}"?`,
      danger: count > 0,
      onConfirm: async () => {
        const deleteRuns_ = count > 0 && window.confirm(`Delete all ${count} runs inside "${g.name}" too?\n\nOK = delete runs, Cancel = keep runs (move to ungrouped)`);
        await deleteRunGroup(g.id, deleteRuns_);
        if (filterGroupId === g.id) setFilterGroupId("all");
        setConfirmDialog(null);
      },
    });
  }

  // ── Move to group ────────────────────────────────────────────────────────
  async function handleMoveToGroup() {
    let targetId: string | undefined;
    if (moveToGroupId === "new") {
      if (!newGroupName.trim()) return;
      const g = await saveRunGroup({ name: newGroupName.trim(), color: newGroupColor });
      targetId = g.id;
    } else if (moveToGroupId === "none") {
      targetId = undefined;
    } else {
      targetId = moveToGroupId;
    }
    await moveRunsToGroup(checkedRuns.map(r => r.id), targetId);
    setShowGroupModal(false);
    setNewGroupName("");
    clearSelection();
  }

  // ── Export helpers ───────────────────────────────────────────────────────
  async function handleExportSingle(run: Run) {
    setExporting(true);
    try {
      const steps = await db.runSteps.where("runId").equals(run.id).toArray();
      await downloadRunReport(run, steps);
    } finally { setExporting(false); }
  }

  async function handleExportAll() {
    if (filtered.length === 0) return;
    setExporting(true);
    try {
      const stepsMap = new Map<string, RunStep[]>();
      for (const run of filtered) {
        const steps = await db.runSteps.where("runId").equals(run.id).toArray();
        stepsMap.set(run.id, steps);
      }
      await downloadRunReportsAsZip(filtered, stepsMap);
    } finally { setExporting(false); }
  }

  // ── Summary counts ───────────────────────────────────────────────────────
  const passed  = runs.filter((r) => r.status === "passed").length;
  const failed  = runs.filter((r) => r.status === "failed").length;
  const healed  = runs.filter((r) => r.status === "passed_healed").length;

  return (
    <div className="reports-tab">

      {/* ── Confirmation dialog ── */}
      {confirmDialog && (
        <div className="rep-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDialog(null); }}>
          <div className={`rep-confirm-box${confirmDialog.danger ? " rep-confirm-box--danger" : ""}`}>
            <h3 className="rep-confirm-title">{confirmDialog.title}</h3>
            <p className="rep-confirm-body">{confirmDialog.body}</p>
            <div className="rep-confirm-actions">
              <button className={`btn${confirmDialog.danger ? " btn--danger" : " btn--primary"}`} onClick={confirmDialog.onConfirm}>
                {confirmDialog.danger ? "⚠ Delete" : "Delete"}
              </button>
              <button className="btn btn--ghost" onClick={() => setConfirmDialog(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Move to Group modal ── */}
      {showGroupModal && (
        <div className="rep-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowGroupModal(false); }}>
          <div className="rep-confirm-box">
            <h3 className="rep-confirm-title">Move {checkedRuns.length} run{checkedRuns.length !== 1 ? "s" : ""} to group</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "12px 0" }}>
              <select className="rep-filter-select" value={moveToGroupId} onChange={e => setMoveToGroupId(e.target.value)}
                style={{ fontSize: "0.85rem", padding: "0.4rem" }}>
                <option value="">— select a group —</option>
                <option value="none">Remove from group (ungrouped)</option>
                <option value="new">+ Create new group…</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({runs.filter(r => r.groupId === g.id).length} runs)</option>)}
              </select>
              {moveToGroupId === "new" && (
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="rep-filter-input" style={{ flex: 1 }} placeholder="Group name (e.g. Release 2.0)" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} autoFocus />
                  <input type="color" value={newGroupColor} onChange={e => setNewGroupColor(e.target.value)} style={{ width: 36, height: 36, border: "none", cursor: "pointer", borderRadius: 4 }} title="Group color" />
                </div>
              )}
            </div>
            <div className="rep-confirm-actions">
              <button className="btn btn--primary" disabled={!moveToGroupId || (moveToGroupId === "new" && !newGroupName.trim())} onClick={() => void handleMoveToGroup()}>Move</button>
              <button className="btn btn--ghost" onClick={() => setShowGroupModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="rep-header">
        <h2 className="rep-title">Reports</h2>
        <div className="rep-summary">
          <span className="rep-summary-chip rep-summary-chip--total">{runs.length} total</span>
          {passed > 0  && <span className="rep-summary-chip rep-summary-chip--passed">✓ {passed}</span>}
          {failed > 0  && <span className="rep-summary-chip rep-summary-chip--failed">✗ {failed}</span>}
          {healed > 0  && <span className="rep-summary-chip rep-summary-chip--healed">⚕ {healed}</span>}
        </div>
        <button className="btn btn--export-rep" onClick={handleExportAll} disabled={exporting || filtered.length === 0}>
          {exporting ? "Exporting…" : `↓ Export Reports (${filtered.length})`}
        </button>
      </div>

      {/* ── Filters ── */}
      <div className="rep-filters">
        <input className="rep-filter-input" placeholder="Search test name or tag…" value={filterText} onChange={(e) => setFilterText(e.target.value)} />
        <select className="rep-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as RunStatus | "all")}>
          <option value="all">All statuses</option>
          <option value="passed">✓ Passed</option>
          <option value="failed">✗ Failed</option>
          <option value="passed_healed">⚕ Healed</option>
          <option value="error">! Error</option>
        </select>
        {groups.length > 0 && (
          <select className="rep-filter-select" value={filterGroupId} onChange={e => setFilterGroupId(e.target.value)}>
            <option value="all">All groups</option>
            <option value="ungrouped">Ungrouped</option>
            {groups.map(g => <option key={g.id} value={g.id}>📁 {g.name}</option>)}
          </select>
        )}
        <input type="date" className="rep-filter-date" title="Run date from" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
        <span className="rep-filter-date-sep">–</span>
        <input type="date" className="rep-filter-date" title="Run date to" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
        {(filterText || filterStatus !== "all" || filterDateFrom || filterDateTo || filterGroupId !== "all" || filterTag) && (
          <button className="rep-filter-clear" onClick={() => { setFilterText(""); setFilterStatus("all"); setFilterDateFrom(""); setFilterDateTo(""); setFilterGroupId("all"); setFilterTag(""); }}>
            ✕ Clear all
          </button>
        )}
        <span className="rep-filter-count">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* ── Tag filter chips ── */}
      {allTags.length > 0 && (
        <div className="rep-tag-filter-bar">
          <span className="rep-tag-filter-label">Tag:</span>
          {allTags.map(t => (
            <button key={t} className={`rep-tag-filter-chip${filterTag === t ? " rep-tag-filter-chip--active" : ""}`}
              onClick={() => setFilterTag(filterTag === t ? "" : t)}>{t}</button>
          ))}
        </div>
      )}

      {/* ── Group sidebar ── */}
      {groups.length > 0 && (
        <div className="rep-groups-bar">
          {groups.map(g => {
            const count = runs.filter(r => r.groupId === g.id).length;
            return (
              <div key={g.id} className={`rep-group-chip${filterGroupId === g.id ? " rep-group-chip--active" : ""}`}
                style={{ "--gc": g.color ?? "#6366f1" } as React.CSSProperties}
                onClick={() => setFilterGroupId(filterGroupId === g.id ? "all" : g.id)}>
                <span className="rep-group-dot" />
                <span>{g.name}</span>
                <span className="rep-group-count">{count}</span>
                <button className="rep-group-delete" onClick={(e) => { e.stopPropagation(); handleDeleteGroup(g); }} title={`Delete group "${g.name}"`}>✕</button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Bulk action bar (shown when rows are checked) ── */}
      {checkedRuns.length > 0 && (
        <div className="rep-bulk-bar">
          <span className="rep-bulk-label">{checkedRuns.length} selected</span>
          <button className="btn" onClick={() => { setMoveToGroupId(""); setShowGroupModal(true); }}>📁 Move to Group</button>
          <button className="btn btn--danger" onClick={handleDeleteSelected}>✕ Delete Selected</button>
          <button className="btn btn--ghost" onClick={clearSelection}>✕ Clear Selection</button>
          {filtered.length > checkedRuns.length && (
            <button className="btn" onClick={toggleAll}>☑ Select All ({filtered.length})</button>
          )}
        </div>
      )}

      {/* ── Delete all filtered ── */}
      {checkedRuns.length === 0 && filtered.length > 0 && filtered.length < runs.length && (
        <div className="rep-delete-filtered-bar">
          <button className="rep-delete-filtered-btn" onClick={handleDeleteAllFiltered} title={`Delete all ${filtered.length} filtered runs`}>
            ✕ Delete all {filtered.length} filtered runs
          </button>
        </div>
      )}

      {/* ── Run table ── */}
      {runs.length === 0 ? (
        <div className="rep-empty">No runs yet. Go to <strong>Execution</strong> and run a test to see results here.</div>
      ) : filtered.length === 0 ? (
        <div className="rep-empty">No runs match the current filters.</div>
      ) : (
        <div className="rep-table-wrapper">
          <table className="rep-table">
            <thead>
              <tr>
                <th className="rep-th-check">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} title="Select all visible runs" />
                </th>
                <th className="rep-th-sortable" onClick={() => toggleSort("date")}>Date {sortIcon("date")}</th>
                <th className="rep-th-sortable" onClick={() => toggleSort("name")}>Test Name {sortIcon("name")}</th>
                <th>Tags</th>
                <th>Device</th>
                <th>Android</th>
                <th className="rep-th-sortable" onClick={() => toggleSort("status")}>Status {sortIcon("status")}</th>
                <th className="rep-th-sortable" onClick={() => toggleSort("steps")}>Steps {sortIcon("steps")}</th>
                <th className="rep-th-sortable" onClick={() => toggleSort("duration")}>Duration {sortIcon("duration")}</th>
                <th style={{ width: 100 }}>Report</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((run) => {
                const group = groups.find(g => g.id === run.groupId);
                return (
                  <>
                    <tr
                      key={run.id}
                      className={`rep-run-row rep-run-row--${run.status}${expandedRunId === run.id ? " rep-run-row--expanded" : ""}${checkedIds.has(run.id) ? " rep-run-row--checked" : ""}`}
                      onClick={() => setExpandedRunId((prev) => prev === run.id ? null : run.id)}
                    >
                      <td className="rep-td-check" onClick={e => { e.stopPropagation(); toggleCheck(run.id); }}>
                        <input type="checkbox" checked={checkedIds.has(run.id)} onChange={() => toggleCheck(run.id)} onClick={e => e.stopPropagation()} />
                      </td>
                      <td className="rep-td-date">
                        {group && <span className="rep-group-badge" style={{ background: `${group.color ?? "#6366f1"}22`, color: group.color ?? "#6366f1", borderColor: `${group.color ?? "#6366f1"}44` }}>{group.name}</span>}
                        {formatDate(run.startedAt)}
                      </td>
                      <td className="rep-td-name">{run.testCaseName}</td>
                      <td className="rep-td-tags">{(run.tags ?? []).map((t) => <span key={t} className="rep-tag">{t}</span>)}</td>
                      <td className="rep-td-device">{run.deviceModel || run.deviceSerial || "—"}</td>
                      <td className="rep-td-android">{run.androidVersion || "—"}</td>
                      <td>
                        <span className={`rep-status rep-status--${run.status}`}>
                          {statusIcon(run.status)} {statusLabel(run.status)}
                          {run.healCount > 0 && ` (${run.healCount} healed)`}
                        </span>
                      </td>
                      <td><span className="rep-steps">{run.passedCount}/{run.stepCount}{run.failedCount > 0 && <span className="rep-steps-fail"> ✗{run.failedCount}</span>}</span></td>
                      <td className="rep-td-duration">{formatDuration(run.startedAt, run.finishedAt)}</td>
                      <td className="rep-td-export" onClick={e => e.stopPropagation()}>
                        <button className="rep-export-btn" title="Download HTML report" onClick={() => void handleExportSingle(run)} disabled={exporting}>↓ HTML</button>
                        <button className="rep-delete-btn" title="Delete this run" onClick={() => handleDeleteOne(run)}>✕</button>
                      </td>
                    </tr>
                    {expandedRunId === run.id && (
                      <tr key={`${run.id}-detail`} className="rep-detail-row">
                        <td colSpan={10}><RunDetail runId={run.id} /></td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── RunDetail ─────────────────────────────────────────────────────────────────

function RunDetail({ runId }: { runId: string }) {
  const steps = useLiveQuery<RunStep[]>(
    () => db.runSteps.where("runId").equals(runId).sortBy("stepIndex"),
    [runId],
  ) ?? [];

  // Load the Run → TestCase → IR to get step names (no schema change needed)
  const run = useLiveQuery(() => db.runs.get(runId), [runId]);
  const testCase = useLiveQuery(
    () => run?.testCaseId ? db.testCases.get(run.testCaseId) : undefined,
    [run?.testCaseId],
  );

  // Build a map from IR step id → human-readable name
  const nameMap = new Map<string, string>();
  if (testCase) {
    for (const flow of testCase.ir.flows) {
      for (const irStep of flow.steps) {
        nameMap.set(irStep.id, irStepName(irStep));
      }
    }
  }

  if (steps.length === 0) {
    return <p className="rep-detail-empty">Loading step detail…</p>;
  }

  return (
    <div className="rep-detail">
      <table className="rep-detail-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Type</th>
            <th>Step Name</th>
            <th>Status</th>
            <th>Matched By</th>
            <th>Coords</th>
            <th>Detail / Hint</th>
            <th style={{ width: 90 }}>Screenshot</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((step) => {
            const stepName = nameMap.get(step.stepId) ?? "—";
            return (
              <tr key={step.id} className={`rep-step-row rep-step-row--${step.status}`}>
                <td>{step.stepIndex + 1}</td>
                <td><span className="rep-step-type">{step.stepType}</span></td>
                <td className="rep-step-name">{stepName}</td>
                <td>
                  <span className={`rep-step-badge rep-step-badge--${step.status}`}>
                    {step.status === "ok" ? "OK"
                     : step.status === "not_found" ? "NOT FOUND"
                     : step.status === "passed_healed" ? "HEALED"
                     : "ERROR"}
                  </span>
                </td>
                <td>{step.matchedBy ? <span className="rep-step-matched">{step.matchedBy}</span> : "—"}</td>
                <td>{step.x !== undefined ? `(${step.x}, ${step.y})` : "—"}</td>
                <td>
                  {step.detail && <span className="rep-step-detail">{step.detail}</span>}
                  {step.healHint && <span className="rep-step-healhint">{step.healHint}</span>}
                  {step.a11yDump && step.status !== "ok" && (
                    <details className="rep-step-log">
                      <summary className="rep-step-log-summary">
                        📋 Accessibility Tree
                        <button
                          className="rep-step-log-dl"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const blob = new Blob([step.a11yDump!], { type: "application/json" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url; a.download = `a11y-step-${step.stepIndex + 1}.json`; a.click();
                            URL.revokeObjectURL(url);
                          }}
                          title="Download accessibility tree as JSON"
                        >↓ JSON</button>
                      </summary>
                      <pre className="rep-step-log-pre">{step.a11yDump}</pre>
                    </details>
                  )}
                </td>
                <td style={{ textAlign: "center" }}>
                  {step.screenshotRef
                    ? <StepScreenshot screenshotRef={step.screenshotRef} />
                    : <span style={{ color: "var(--color-text-muted)", fontSize: "0.7rem" }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── StepScreenshot ────────────────────────────────────────────────────────────

function StepScreenshot({ screenshotRef }: { screenshotRef: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    readScreenshotAsBase64(screenshotRef).then((b64) => {
      if (!cancelled && b64) setSrc(`data:image/png;base64,${b64}`);
    });
    return () => { cancelled = true; };
  }, [screenshotRef]);

  if (!src) return <span style={{ color: "var(--color-text-muted)", fontSize: "0.65rem" }}>loading…</span>;

  return (
    <>
      <img
        src={src}
        alt="step screenshot"
        style={{ maxWidth: 70, maxHeight: 56, borderRadius: 3, cursor: "pointer", display: "block", margin: "0 auto" }}
        onClick={() => setExpanded((v) => !v)}
        title="Click to expand"
      />
      {expanded && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out" }}
          onClick={() => setExpanded(false)}
        >
          <img src={src} alt="step screenshot" style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 6 }} />
        </div>
      )}
    </>
  );
}
